import * as yup from 'yup'
import React from 'react'
import { Divider } from '../../../../components/divider'
import { suffixes } from '../../../../choices'

import {
  addressLabel,
  address2Label,
  cellPhoneLabel,
  employerLabel,
  employerPhoneLabel,
  firstNameLabel,
  homePhoneLabel,
  jobTitleLabel,
  lastNameLabel,
  middleNameLabel,
  optionalLabel,
  personalEmailLabel,
  suffixLabel,
  workEmailLabel,
  workPhoneLabel
} from '../../../../strings/labels'
import {
  addressLengthMessage,
  cellPhoneMessage,
  cityLengthMessage,
  employerPhoneMessage,
  firstNameMessage,
  firstNameLengthMessage,
  homePhoneMessage,
  jobTitleLengthMessage,
  lastNameMessage,
  lastNameLengthMessage,
  middleNameMessage,
  middleNameLengthMessage,
  personalEmailMessage,
  personalEmailLengthMessage,
  workEmailLengthMessage,
  workEmailMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage,
  requiredMessage
} from '../../../../strings/messages'
import { alphaRegex, phoneRegex, zipRegex } from '../../../../strings/regex'
import { IHSVictimChoiceField } from '../../../../components/IHSVictimChoiceField/IHSVictimChoiceField'

const NameHeading = () => <h2 className="ds-u-margin-bottom--0">Name</h2>

const ContactInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Contact information</h2>
)

const ContentDivider = () => <Divider />

const EmploymentInformationHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Employment information</h2>
)

const VictimHeading = () => (
  <h2 className="ds-u-margin-bottom--0">Relationship to Victim(s)</h2>
)

export const parentGuardianPage = propOverrides => {
  const props = {
    index: 0,
    maxIndex: 9,
    pageId: 'parentguardian_page',
    title: 'Parent/Guardian Information',
    description:
      'Please provide as much contact details as possible. We need a phone number, email address, or mailing address to contact them. You can enter multiple Parent/Guardians',
    pageAfterParentGuardian: 'permission_to_disclose_page',
    ...propOverrides
  }

  // Create unique field names based on the field name and the index value.
  const pageId = `${props.pageId}_${props.index}`
  const first_name = `parentguardian_first_name_${props.index}`
  const middle_name = `parentguardian_middle_name_${props.index}`
  const last_name = `parentguardian_last_name_${props.index}`
  const suffix = `parentguardian_suffix_${props.index}`
  const home_phone = `parentguardian_home_phone_${props.index}`
  const cell_phone = `parentguardian_cell_phone_${props.index}`
  const work_phone = `parentguardian_work_phone_${props.index}`
  const personal_email = `parentguardian_personal_email_${props.index}`
  const work_email = `parentguardian_work_email_${props.index}`
  const address = `parentguardian_address_${props.index}`
  const job_title = `parentguardian_job_title_${props.index}`
  const employer = `parentguardian_employer_${props.index}`
  const employer_address = `parentguardian_employer_address_${props.index}`
  const employer_phone = `parentguardian_employer_phone_${props.index}`
  const victims = `parentguardian_victims_${props.index}`

  let page = {
    id: pageId,
    title: props.title,
    description: props.description,
    validation: yup.object().shape({
      [victims]: yup.array().required(requiredMessage),
      [first_name]: yup
        .string()
        .matches(alphaRegex, {
          message: firstNameMessage,
          excludeEmptyString: false
        })
        .max(30, firstNameLengthMessage)
        .required(firstNameMessage),
      [middle_name]: yup
        .string()
        .matches(alphaRegex, {
          message: middleNameMessage,
          excludeEmptyString: false
        })
        .max(30, middleNameLengthMessage),
      [last_name]: yup
        .string()
        .matches(alphaRegex, {
          message: lastNameMessage,
          excludeEmptyString: false
        })
        .max(30, lastNameLengthMessage)
        .required(lastNameMessage),
      [home_phone]: yup.string().matches(phoneRegex, {
        message: homePhoneMessage,
        excludeEmptyString: false
      }),
      [cell_phone]: yup.string().matches(phoneRegex, {
        message: cellPhoneMessage,
        excludeEmptyString: false
      }),
      [work_phone]: yup.string().matches(phoneRegex, {
        message: workPhoneMessage,
        excludeEmptyString: false
      }),
      [personal_email]: yup
        .string()
        .email(personalEmailMessage)
        .max(250, personalEmailLengthMessage),
      [work_email]: yup
        .string()
        .email(workEmailMessage)
        .max(250, workEmailLengthMessage),
      [address]: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      [job_title]: yup.string().max(30, jobTitleLengthMessage),
      [employer_address]: yup.object().shape({
        line1Value: yup.string().max(50, addressLengthMessage),
        line2Value: yup.string().max(50, addressLengthMessage),
        cityValue: yup.string().max(30, cityLengthMessage),
        zipValue: yup
          .string()
          .max(10, zipLengthMessage)
          .test('zipcode_required', zipMessage, function(value) {
            const { countryValue } = this.parent
            return countryValue === 'US'
              ? value === '' || zipRegex.test(value)
              : true
          })
      }),
      [employer_phone]: yup.string().matches(phoneRegex, {
        message: employerPhoneMessage,
        excludeEmptyString: false
      })
    }),
    fields: [
      {
        type: 'content',
        content: VictimHeading,
        name: 'victim_information'
      },
      {
        type: 'custom',
        label:
          'Please select the Victim(s) this Parent or Guardian is responsible for',
        name: victims,
        content: IHSVictimChoiceField
      },
      {
        type: 'content',
        content: NameHeading,
        name: 'name_information'
      },
      {
        type: 'text',
        label: firstNameLabel,
        name: first_name
      },
      {
        type: 'text',
        label: middleNameLabel,
        name: middle_name,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: lastNameLabel,
        name: last_name
      },
      {
        type: 'select',
        label: suffixLabel,
        name: suffix,
        choices: [
          {
            value: '',
            label: ''
          },
          ...suffixes()
        ],
        size: 'small',
        requirementLabel: optionalLabel
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider1'
      },
      {
        type: 'content',
        content: ContactInformationHeading,
        name: 'contact_information'
      },
      {
        type: 'text',
        label: homePhoneLabel,
        name: home_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: cellPhoneLabel,
        name: cell_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workPhoneLabel,
        name: work_phone,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: personalEmailLabel,
        name: personal_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: workEmailLabel,
        name: work_email,
        requirementLabel: optionalLabel
      },
      {
        type: 'address',
        name: address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'content',
        content: ContentDivider,
        name: 'divider3'
      },
      {
        type: 'content',
        content: EmploymentInformationHeading,
        name: 'employment_information'
      },
      {
        type: 'text',
        label: jobTitleLabel,
        name: job_title,
        requirementLabel: optionalLabel
      },
      {
        type: 'text',
        label: employerLabel,
        name: employer,
        requirementLabel: optionalLabel
      },
      {
        type: 'address',
        name: employer_address,
        label: '',
        hint: '',
        line1Label: addressLabel,
        line2Label: address2Label,
        line1RequirementLabel: optionalLabel,
        line2RequirementLabel: optionalLabel,
        cityRequirementLabel: optionalLabel,
        stateRequirementLabel: optionalLabel,
        zipRequirementLabel: optionalLabel,
        countryValue: 'US'
      },
      {
        type: 'text',
        label: employerPhoneLabel,
        name: employer_phone,
        requirementLabel: optionalLabel
      }
    ]
  }

  // When not on the last page, give option to add another victim
  if (props.index < props.maxIndex) {
    const add_parent_guardian = `add_parentguardian_${props.index}`
    const nextParentGuardianPage = `${props.pageId}_${props.index + 1}`
    page.fields.push({
      type: 'checkbox',
      label: '',
      name: add_parent_guardian,
      choices: [
        {
          label: `Add another parent/guardian (up to ${props.maxIndex +
            1} parent/guardians)`,
          value: 'True'
        }
      ],
      requirementLabel: optionalLabel
    })
    page.action = {
      type: 'goto',
      pageId: values => {
        const add = values[add_parent_guardian]
        return add.length > 0
          ? nextParentGuardianPage
          : props.pageAfterParentGuardian
      }
    }
  }

  return page
}
